import React, { createContext, useContext, useState, useCallback } from 'react';
import { Address, CardDisplayInfo } from '../../../types';
import { SignupInvoicePreview } from '../../../services/membershipApi/signup/previewInvoice';

export type BillingCycle = 'monthly' | 'annual';

interface PaymentContextType {
    // Payment method
    paymentMethodId: string | null;
    cardDisplayInfo: CardDisplayInfo | null;
    setPaymentMethod: (id: string, cardInfo: CardDisplayInfo) => void;
    clearPaymentMethod: () => void;

    // Addresses
    billingAddress: Address;
    shippingAddress: Address;
    updateBillingAddress: (address: Partial<Address>) => void;
    updateShippingAddress: (address: Partial<Address>) => void;

    // Billing cycle
    billingCycle: BillingCycle;
    setBillingCycle: (cycle: BillingCycle) => void;

    // Shipping preference
    shippingSameAsBilling: boolean;
    setShippingSameAsBilling: (same: boolean) => void;

    // Terms agreement
    termsAgreed: boolean;
    setTermsAgreed: (agreed: boolean) => void;

    // Processing state
    isProcessing: boolean;
    setIsProcessing: (processing: boolean) => void;

    // Invoice preview
    invoicePreview: SignupInvoicePreview | null;
    setInvoicePreview: (preview: SignupInvoicePreview | null) => void;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

const initialAddress: Address = {
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
};

export const PaymentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
    const [cardDisplayInfo, setCardDisplayInfo] = useState<CardDisplayInfo | null>(null);
    const [billingAddress, setBillingAddress] = useState<Address>(initialAddress);
    const [shippingAddress, setShippingAddress] = useState<Address>(initialAddress);
    const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
    const [shippingSameAsBilling, setShippingSameAsBilling] = useState(true);
    const [termsAgreed, setTermsAgreed] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [invoicePreview, setInvoicePreview] = useState<SignupInvoicePreview | null>(null);

    const setPaymentMethod = useCallback((id: string, cardInfo: CardDisplayInfo) => {
        setPaymentMethodId(id);
        setCardDisplayInfo(cardInfo);
    }, []);

    const clearPaymentMethod = useCallback(() => {
        setPaymentMethodId(null);
        setCardDisplayInfo(null);
    }, []);

    const updateBillingAddress = useCallback((address: Partial<Address>) => {
        setBillingAddress(prev => ({ ...prev, ...address }));
    }, []);

    const updateShippingAddress = useCallback((address: Partial<Address>) => {
        setShippingAddress(prev => ({ ...prev, ...address }));
    }, []);

    const value: PaymentContextType = {
        paymentMethodId,
        cardDisplayInfo,
        setPaymentMethod,
        clearPaymentMethod,
        billingAddress,
        shippingAddress,
        updateBillingAddress,
        updateShippingAddress,
        billingCycle,
        setBillingCycle,
        shippingSameAsBilling,
        setShippingSameAsBilling,
        termsAgreed,
        setTermsAgreed,
        isProcessing,
        setIsProcessing,
        invoicePreview,
        setInvoicePreview,
    };

    return (
        <PaymentContext.Provider value={value}>
            {children}
        </PaymentContext.Provider>
    );
};

export const usePaymentContext = (): PaymentContextType => {
    const context = useContext(PaymentContext);
    if (!context) {
        throw new Error('usePaymentContext must be used within a PaymentProvider');
    }
    return context;
};
